import { pick, get, pickBy, isEqual } from 'lodash';
import up from 'updeep';
import * as userAPI from '../../lib/userApi';
import patch from './patch';
import tokenNoLongerValid from './tokenNoLongerValid';
import Logger from '../../Logger';
import { parseToLocationSearch } from '../../lib/locations';
import selectors from '../selectors';
import { handleUnexpectedError } from './handleUnexpectedError';
// import { getPlaceGeocode } from '../../lib/googleMaps';

export const assignLeadersFilterForm = update => patch({
  leaders: { filterForm: update },
});

const makeCalculatorResultLeaderQuery = (defaultOrder, leadersFilter, includeSeedUsers) => {
  let location = {};
  if (leadersFilter.location) location = parseToLocationSearch(leadersFilter.location);
  const query = pickBy({
    order: leadersFilter.order || defaultOrder,
    ...pick(leadersFilter, ['inputSize']),
    ...location,
  });
  if (includeSeedUsers) query.includeSeedUsers = true;
  return query;
};

export const pageLeaders = (currentRoute, page) => async (dispatch, getState) => {
  const state = getState();
  const order = selectors.leadersOrder(state, { currentRoute });
  const { leaders } = state;
  if (get(leaders, `queriesByOrder.${order}.pages.${page}`)) {
    dispatch(patch({
      leaders: { queriesByOrder: { [order]: { currentPage: page } } },
    }));
    return;
  }
  dispatch(patch({ ui: { spinner: true } }));
  try {
    const includeSeedUsers = get(getState(), 'search.includeSeedUsers', false);
    const query = makeCalculatorResultLeaderQuery(order, leaders.filter, includeSeedUsers);
    query.page = page;
    const res = await userAPI.getCalculatorResultLeaders(query);
    const {
      nextPage,
      data,
      totalPages,
    } = res.body;
    const currentPage = nextPage === -1 ? totalPages : nextPage - 1;
    dispatch(patch({
      ui: { spinner: false },
      leaders: { queriesByOrder: {
        [order]: {
          currentPage,
          nextPage,
          totalPages,
          pages: { [currentPage]: data },
        },
      } },
    }));
  } catch (err) {
    if (err.response && err.response.unauthorized) {
      dispatch(tokenNoLongerValid());
      return;
    }
    dispatch(patch({ ui: { spinner: false } }));
    handleUnexpectedError(err)(dispatch, getState);
  }
};

const applyLeadersFilter = (currentRoute, spinner = true) => async (dispatch, getState) => {
  if (spinner) dispatch(patch({ ui: { spinner: true } }));
  let state = getState();
  const order = selectors.leadersOrder(state, { currentRoute });
  const { filterForm, filter } = state.leaders;
  try {
    let filterUpdate = filterForm;
    if (filterForm.location && filterForm.location.key !== get(filter, 'location.key')) {
      // const [geocode] = await getPlaceGeocode(filterForm.location.place_id);
      filterUpdate = {
        ...filter,
        location: filterForm.location,
      };
    }
    dispatch(patch({
      leaders: {
        filter: filterUpdate,
        queries: {
          [order]: {
            initialized: true,
            totalPages: 0,
            nextPage: -1,
            currentPage: 0,
            pages: {},
          },
        },
      },
    }));
    const includeSeedUsers = get(state, 'search.includeSeedUsers', false);
    state = getState();
    const query =
      makeCalculatorResultLeaderQuery(order, state.leaders.filter, includeSeedUsers);
    const res = await userAPI.getCalculatorResultLeaders(query);
    const {
      nextPage,
      totalPages,
      data,
    } = res.body;
    const update = {
      leaders: {
        queriesByOrder: {
          [order]: {
            nextPage,
            totalPages,
            currentPage: 1,
            pages: { 1: data },
          },
        },
      },
    };
    if (spinner) update.ui = { spinner: false };
    dispatch(patch(update));
  } catch (err) {
    if (err.response && err.response.unauthorized) {
      dispatch(tokenNoLongerValid());
      return;
    }
    if (spinner) {
      dispatch(patch({ ui: { spinner: false } }));
    }
    handleUnexpectedError(err)(dispatch, getState);
  }
};

// NOTE: After applying the leaders filter form, we need to re-initialize
// all queries.
export const updateLeadersFilter = currentRoute => async (dispatch, getState) => {
  const { filter, filterForm } = getState().leaders;
  if (isEqual(filter, filterForm)) return;

  dispatch(patch({
    leaders: { queriesByOrder: up.constant({}) },
  }));
  await applyLeadersFilter(currentRoute, true)(dispatch, getState);
};

// NOTE: After updating the filter order, we do not need to re-initialize
// the queries because they are cached by the order string.
export const toggleLeadersOrder = (currentRoute, orderField) =>
  async (dispatch, getState) => {
    const state = getState();
    let orderDesc = false;
    const currentOrderField = state.leaders.filter.orderField;
    if (orderField === 'pledge_count') {
      if (currentOrderField !== 'pledge_count' || !state.leaders.filter.orderDesc) {
        orderDesc = true;
      }
    } else if (currentOrderField !== 'pledge_count' && !state.leaders.filter.orderDesc) {
      orderDesc = true;
    }
    dispatch(patch({
      leaders: { filter: { orderField, orderDesc } },
    }));
    const order = selectors.leadersOrder(getState(), { currentRoute });
    if (get(getState(), `leaders.queriesByOrder.${order}.initialized`)) {
      return;
    }
    await applyLeadersFilter(currentRoute, true)(dispatch, getState);
  };

export const initializeLeadersForRoute = newRoute => async (dispatch, getState) => {
  const order = selectors.leadersOrder(getState(), { currentRoute: newRoute });
  if (get(getState(), `leaders.queriesByOrder.${order}.initialized`)) {
    return;
  }
  await applyLeadersFilter(newRoute, false)(dispatch, getState);
};

export const toggleModalLeader = user => (dispatch, _getState) => {
  dispatch(patch({
    ui: {
      modal: user ? 'leader' : '',
      modalLeader: user,
    },
  }));
};

export const setModalLeader = user => async (dispatch, _getState) => {
  dispatch(patch({ ui: { modal: 'leader', modalLeader: user } }));
  try {
    const { body: { pledges } } = await userAPI.getUserPledges(user.id);
    dispatch(patch({
      modalLeaderPledges: {
        [user.id]: pledges,
      },
    }));
  } catch (err) {
    Logger.error(err);
  }
};
